import {
  TicketStatus,
  type CreateTicketPayload,
  type Ticket,
} from "#/types/tickets";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, Paper, Skeleton, Title } from "@mantine/core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { TicketCard } from "./TicketCard";

interface KanbanColumnProps {
  keyId: TicketStatus;
  items: Ticket[];
  isOver: boolean;
  loading: boolean;
  updatingTicketId: number | null;
  updateTicket: (id: number, payload: Partial<CreateTicketPayload>) => void;
}

const KanbanColumn = ({
  keyId,
  items,
  isOver,
  loading,
  updatingTicketId,
  updateTicket,
}: KanbanColumnProps) => {
  const { t } = useTranslation();
  const { setNodeRef } = useDroppable({ id: keyId });

  return (
    <Paper
      ref={setNodeRef}
      key={keyId}
      radius="md"
      className={clsx(
        "bg-blue-50 transition-all duration-200 h-full overflow-hidden w-64 flex-shrink-0 lg:w-full flex flex-col",
        isOver &&
          `scale-x-105 shadow-xl z-10 ${
            keyId === TicketStatus.Requested
              ? "bg-blue-100"
              : keyId === TicketStatus.InProgress
                ? "bg-yellow-50"
                : keyId === TicketStatus.Done
                  ? "bg-green-50"
                  : "bg-red-50"
          }`
      )}
      shadow="sm"
      id={keyId}
    >
      {/* Sticky Header */}
      <Box
        p="md"
        bg={
          keyId === TicketStatus.Requested
            ? "blue.1"
            : keyId === TicketStatus.InProgress
              ? "yellow.1"
              : keyId === TicketStatus.Done
                ? "green.1"
                : "red.1"
        }
      >
        <Title
          order={6}
          ta="center"
          c={
            keyId === TicketStatus.Requested
              ? "blue.6"
              : keyId === TicketStatus.InProgress
                ? "yellow.7"
                : keyId === TicketStatus.Done
                  ? "green.7"
                  : "red.7"
          }
        >
          {keyId === TicketStatus.Requested && t("tickets.columns.requested")}
          {keyId === TicketStatus.InProgress && t("tickets.columns.inProgress")}
          {keyId === TicketStatus.Done && t("tickets.columns.done")}
          {keyId === TicketStatus.ProblemTickets && t("tickets.columns.problemTickets")}
        </Title>
      </Box>
      <div className="flex-1 overflow-y-auto py-4">
        {loading && (
          <div className="flex flex-col gap-4 px-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={100} radius={12} />
            ))}
          </div>
        )}
        <SortableContext
          items={
            items.length > 0
              ? items.map((item) => item.id.toString())
              : ["__empty__"]
          }
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <TicketCard
              key={item.id}
              ticket={item}
              isUpdating={updatingTicketId === item.id}
              onMarkTicketAsProblem={(payload) =>
                updateTicket(item.id, payload)
              }
            />
          ))}
        </SortableContext>
      </div>
    </Paper>
  );
};

export default KanbanColumn;
